import { appendOutput, markRunFailed, markRunFinished, runLabel, runStatus } from './output';
import type { WorkerInbound, WorkerOutbound } from './python-worker';

let worker: Worker | null = null;
let sequence = 0;
let active: { id: number; resolve: () => void } | null = null;

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('./python-worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (event: MessageEvent<WorkerOutbound>) => {
    const message = event.data;
    switch (message.type) {
      case 'status':
        runLabel.value = message.text;
        runStatus.value = message.text === 'Running' ? 'running' : 'loading';
        break;
      case 'ready':
        break;
      case 'stdout':
      case 'stderr':
      case 'info':
        appendOutput({ kind: message.type, text: message.text });
        break;
      case 'figure':
        appendOutput({ kind: 'figure', src: `data:image/png;base64,${message.png}` });
        break;
      case 'result':
        appendOutput({ kind: 'result', text: message.text });
        break;
      case 'done':
        finish(message.id);
        break;
      case 'error':
        appendOutput({ kind: 'error', text: message.message });
        markRunFailed();
        finish(message.id);
        break;
    }
  };
  worker.onerror = (event) => {
    appendOutput({ kind: 'error', text: `Python worker failed: ${event.message}` });
    markRunFailed();
    if (active) finish(active.id);
  };
  return worker;
}

function finish(id: number): void {
  if (active && active.id === id) {
    const { resolve } = active;
    active = null;
    runStatus.value = 'idle';
    runLabel.value = '';
    markRunFinished();
    resolve();
  }
}

/** Run a Python program. Resolves when the run finishes (successfully or not). */
export function runPython(code: string): Promise<void> {
  if (active) return Promise.resolve();
  const id = ++sequence;
  runStatus.value = 'loading';
  runLabel.value = 'Starting';
  return new Promise<void>((resolve) => {
    active = { id, resolve };
    const message: WorkerInbound = { type: 'run', id, code };
    ensureWorker().postMessage(message);
  });
}

/** Pyodide cannot be interrupted without shared memory, so stopping means discarding the worker. */
export function stopPython(): void {
  if (!worker) return;
  worker.terminate();
  worker = null;
  appendOutput({ kind: 'info', text: 'Stopped. The Python runtime will reload on the next run.\n' });
  if (active) finish(active.id);
}
